import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { JointBillContext } from "@features/JointBillList/utils";
import { IconButton } from "@components/IconButton/IconButton";
import { BinIcon, ClockIcon, DocumentIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";

interface IRequestedProviderBillNameCellProps {
  variant: "serviceProvider" | "powerOfAttorney";
  name: JSX.Element | string;
  statusBadge?: JSX.Element | string;
  isRemoveProviderEnabled?: boolean;
  isRemoveProviderLoading?: boolean;
  onRemoveProviderClick?: () => void;
  shouldDisplayRemoveButton?: boolean;
  removeButtonTooltipContent?: string;
  shouldDisplayDocumentButton?: boolean;
  isDocumentButtonInteractionLoading?: boolean;
  onDocumentButtonClick?: () => void;
  documentButtonTooltipContent?: string;
}

export const RequestedProviderBillNameCell: React.FC<
  IRequestedProviderBillNameCellProps
> = ({
  variant,
  name,
  statusBadge,
  isRemoveProviderEnabled,
  isRemoveProviderLoading,
  shouldDisplayRemoveButton,
  removeButtonTooltipContent,
  onRemoveProviderClick,
  shouldDisplayDocumentButton,
  isDocumentButtonInteractionLoading,
  onDocumentButtonClick,
  documentButtonTooltipContent,
}) => {
  const { t } = useTranslation();
  const { isSummaryMode } = useContext(JointBillContext);

  return (
    <div className="flex h-full flex-row items-start md:items-center">
      <div className="flex min-w-0 gap-3">
        <div className="flex min-w-0 flex-1 flex-shrink flex-col gap-0.5">
          <span
            className="truncate text-sm leading-6 md:text-base"
            title={typeof name === "string" ? name : ""}
          >
            {name}
          </span>
          <div className="md:hidden">{statusBadge}</div>
        </div>
        {!isSummaryMode && (
          <div className="hidden gap-2 md:flex md:items-center">
            <Tooltip
              content={t(
                `bill.requestedSp.list.${variant}.requestInProgress.tooltip`,
              )}
            >
              <ClockIcon className="size-4 text-grey-dark group-hover/requested-bill:text-base-black group-active/requested-bill:text-base-black" />
            </Tooltip>
            {shouldDisplayDocumentButton && (
              <Tooltip content={documentButtonTooltipContent}>
                {(props) => (
                  <IconButton
                    isLoading={isDocumentButtonInteractionLoading}
                    {...props}
                    icon={DocumentIcon}
                    iconClassName="size-4 group-hover/requested-bill:!text-base-black group-active/requested-bill:!text-base-black group-hover:text-grey-dark group-active:text-grey-dark"
                    onClick={onDocumentButtonClick}
                    aria-label={documentButtonTooltipContent}
                  />
                )}
              </Tooltip>
            )}
            {shouldDisplayRemoveButton && (
              <Tooltip content={removeButtonTooltipContent}>
                {(props) => (
                  <IconButton
                    disabled={!isRemoveProviderEnabled}
                    {...props}
                    icon={BinIcon}
                    iconClassName="size-4 group-hover/requested-bill:!text-red-dark group-active/requested-bill:!text-base-black group-hover:text-grey-dark group-active:text-grey-dark"
                    onClick={onRemoveProviderClick}
                    isLoading={isRemoveProviderLoading}
                    aria-label={removeButtonTooltipContent}
                  />
                )}
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
